import { useEffect, useState } from "react";
import {
  Users, UserPlus, Trash2, Mail, Clock, CheckCircle2, X, Copy, Check,
  Settings as SettingsIcon, Eye, EyeOff,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * HelpersManager — owner-side UI for inviting / listing / revoking helpers
 * on a single profile. Used inside the Privacy Dashboard, one accordion
 * per profile owned by the current user.
 *
 * Role-aware permission presets:
 *   caregiver_helper → ["view", "edit_tiles"]
 *   therapist        → ["view"]   (history is opt-in, requires explicit add)
 *
 * After a successful invite, we surface the share-able accept link so the
 * owner can text/email it. We do NOT yet drive Resend automatically — the
 * email pipeline is sandboxed in launch mode.
 */

const ROLE_OPTIONS = [
  { id: "caregiver_helper", label: "Family helper", hint: "Grandma, co-parent, sibling" },
  { id: "therapist",        label: "Therapist",        hint: "SLP, special-ed teacher · suggests changes, you Approve & Apply" },
  { id: "school_official",  label: "School",           hint: "Teacher, classroom aide · auto-applies after your one-time consent" },
];

const STATUS_BADGE = {
  pending:  { label: "Pending",  bg: "var(--gold)" },
  accepted: { label: "Active",   bg: "#D6F5C8" },
  revoked:  { label: "Revoked",  bg: "var(--paper-muted)" },
};

function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function inviteUrl(token) {
  return `${window.location.origin}/helpers/accept/${token}`;
}

export default function HelpersManager({ profile }) {
  const [helpers, setHelpers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", role: "caregiver_helper", note: "" });
  const [lastLink, setLastLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/profiles/${profile.id}/helpers`);
      setHelpers(data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [profile.id]);

  const submit = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError("");
    setLastLink(null);
    try {
      const { data } = await api.post(`/profiles/${profile.id}/helpers/invite`, {
        email: form.email.trim().toLowerCase(),
        role: form.role,
        note: form.note.trim() || null,
      });
      // Surface the accept link the owner can copy. Token isn't returned by
      // /list, so we capture it here from the invite response.
      if (data?.invite_token) {
        setLastLink(inviteUrl(data.invite_token));
      } else {
        // Existing pending re-used (idempotent path) — fetch list to refresh.
        await load();
      }
      setForm({ email: "", role: "caregiver_helper", note: "" });
      setShowForm(false);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setInviting(false);
  };

  const revoke = async (h) => {
    if (!window.confirm(`Revoke ${h.helper_email}'s access to ${profile.name}?`)) return;
    try {
      await api.delete(`/profiles/${profile.id}/helpers/${h.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const [editingPerms, setEditingPerms] = useState(null);  // helper.id being edited

  const updatePermissions = async (h, newPerms) => {
    try {
      await api.patch(`/profiles/${profile.id}/helpers/${h.id}`, {
        permissions: newPerms,
      });
      await load();
      setEditingPerms(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleHelperPerm = (h, key) => {
    const current = new Set(h.permissions || []);
    if (current.has(key)) current.delete(key); else current.add(key);
    if (current.size === 0) current.add("view");  // can't drop below view
    updatePermissions(h, Array.from(current));
  };

  const copyLink = async () => {
    if (!lastLink) return;
    try {
      await navigator.clipboard.writeText(lastLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — link is still selectable */
    }
  };

  return (
    <div
      className="brutal-card bg-white p-4 mb-2"
      data-testid={`helpers-manager-${profile.id}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-9 h-9 rounded-xl border-[2.5px] border-[color:var(--ink)] flex items-center justify-center text-xl"
          style={{ background: profile.color }}
          aria-hidden
        >
          {profile.avatar}
        </span>
        <div className="flex-1 min-w-0">
          <div className="display font-extrabold truncate">{profile.name}</div>
          <div className="text-xs text-[color:var(--ink-soft)]">
            {helpers.filter((h) => h.status !== "revoked").length} helper(s) with access
          </div>
        </div>
        <button
          type="button"
          className="pill-btn primary"
          onClick={() => { setShowForm((s) => !s); setLastLink(null); }}
          data-testid={`helpers-invite-toggle-${profile.id}`}
        >
          <UserPlus size={14} /> {showForm ? "Cancel" : "Invite"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          className="brutal-card bg-[color:var(--cream-deep)] p-3 mb-3"
          data-testid={`helpers-invite-form-${profile.id}`}
        >
          <label className="block mb-2">
            <span className="display font-semibold mb-1 block text-sm">Email</span>
            <input
              type="email"
              required
              className="brutal-input"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="grandma@example.com"
              data-testid={`helpers-invite-email-${profile.id}`}
            />
          </label>
          <div className="mb-2">
            <span className="display font-semibold mb-1 block text-sm">Role</span>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setForm((f) => ({ ...f, role: r.id }))}
                  className={`pill-btn ${form.role === r.id ? "gold" : ""}`}
                  data-testid={`helpers-invite-role-${r.id}`}
                >
                  <span className="font-semibold">{r.label}</span>
                  <span className="text-[11px] text-[color:var(--ink-soft)] hidden sm:inline">{r.hint}</span>
                </button>
              ))}
            </div>
          </div>
          <label className="block mb-3">
            <span className="display font-semibold mb-1 block text-sm">Note (optional)</span>
            <input
              type="text"
              className="brutal-input"
              maxLength={300}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="What you'd like them to help with"
              data-testid={`helpers-invite-note-${profile.id}`}
            />
          </label>
          <button
            type="submit"
            className="pill-btn primary"
            disabled={inviting || !form.email.trim()}
            data-testid={`helpers-invite-submit-${profile.id}`}
          >
            {inviting ? "Inviting…" : "Send invite"}
          </button>
          <p className="text-[11px] text-[color:var(--ink-soft)] mt-2 leading-snug">
            We'll create a one-tap accept link. They sign in (or create an
            account) to accept. You can revoke any time.
          </p>
        </form>
      )}

      {lastLink && (
        <div
          className="brutal-card bg-[color:var(--gold)] p-3 mb-3 text-sm"
          data-testid={`helpers-invite-link-${profile.id}`}
        >
          <div className="display font-bold mb-1 flex items-center gap-1">
            <Mail size={14} /> Send this link to your helper
          </div>
          <div className="flex gap-2">
            <input
              className="brutal-input flex-1 font-mono text-xs"
              readOnly
              value={lastLink}
              onFocus={(e) => e.target.select()}
              data-testid={`helpers-invite-link-input-${profile.id}`}
            />
            <button
              type="button"
              onClick={copyLink}
              className="pill-btn"
              data-testid={`helpers-invite-link-copy-${profile.id}`}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-2 text-sm mb-2" data-testid={`helpers-error-${profile.id}`}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[color:var(--ink-soft)]">Loading helpers…</div>
      ) : helpers.length === 0 ? (
        <div className="text-sm text-[color:var(--ink-soft)] italic">
          No helpers yet. Invite a grandparent, co-parent, or therapist to support {profile.name}'s communication.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {helpers.map((h) => {
            const badge = STATUS_BADGE[h.status] || STATUS_BADGE.pending;
            const Icon = h.status === "accepted" ? CheckCircle2 : h.status === "revoked" ? X : Clock;
            return (
              <li
                key={h.id}
                className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-xl border-[2px] border-[color:var(--ink)] bg-[color:var(--paper-muted)]"
                data-testid={`helper-row-${h.id}`}
              >
                <Icon size={14} className="shrink-0" />
                <div className="flex-1 min-w-[12rem]">
                  <div className="display font-bold text-sm truncate">{h.helper_email}</div>
                  <div className="text-[11px] text-[color:var(--ink-soft)] flex flex-wrap gap-x-2">
                    <span>{h.role.replace(/_/g, " ")}</span>
                    <span>· can: {h.permissions.join(", ")}</span>
                    <span>· invited {fmtTs(h.invited_at)}</span>
                  </div>
                </div>
                <span
                  className="text-[10px] uppercase tracking-[0.18em] font-extrabold px-2 py-0.5 rounded-full border-[2px] border-[color:var(--ink)]"
                  style={{ background: badge.bg }}
                >
                  {badge.label}
                </span>
                {h.status !== "revoked" && (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditingPerms(editingPerms === h.id ? null : h.id)}
                      className="pill-btn"
                      data-testid={`helpers-edit-perms-${h.id}`}
                      title="Edit permissions"
                    >
                      <SettingsIcon size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => revoke(h)}
                      className="pill-btn danger"
                      data-testid={`helpers-revoke-${h.id}`}
                    >
                      <Trash2 size={13} /> Revoke
                    </button>
                  </>
                )}
                {editingPerms === h.id && h.status !== "revoked" && (
                  <div className="basis-full mt-2 pt-2 border-t-2 border-[color:var(--ink)] flex flex-wrap gap-2">
                    {[
                      { key: "view",         label: "View board",       icon: Eye },
                      { key: "edit_tiles",   label: "Edit tiles",       icon: SettingsIcon },
                      { key: "view_history", label: "View history",     icon: Eye },
                    ].map((opt) => {
                      const Icon = opt.icon;
                      const active = (h.permissions || []).includes(opt.key);
                      const isView = opt.key === "view";
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          disabled={isView}
                          onClick={() => toggleHelperPerm(h, opt.key)}
                          className={`pill-btn ${active ? "gold" : ""} ${isView ? "opacity-60 cursor-not-allowed" : ""}`}
                          data-testid={`helpers-perm-toggle-${h.id}-${opt.key}`}
                          title={isView ? "View access is required for any helper role." : ""}
                        >
                          {active ? <CheckCircle2 size={12} /> : <Icon size={12} />}
                          {opt.label}
                        </button>
                      );
                    })}
                    <span className="text-[11px] text-[color:var(--ink-soft)] basis-full">
                      <strong>view_history</strong> lets this helper see {profile.name}'s communication
                      history (only matters when history is opted in). Revokable.
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
